import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { useState, useEffect } from "react";
import img13 from "../assets/images/img13.avif";

// ── Validation helpers ──
function validateName(v) {
  if (!v.trim()) return "Name is required.";
  if (v.trim().length < 2) return "Name must be at least 2 characters.";
  return "";
}
function validateEmail(v) {
  if (!v.trim()) return "Email is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Please enter a valid email address.";
  return "";
}
function validatePhone(v) {
  if (!v.trim()) return "";
  const digits = v.replace(/\D/g, "");
  if (digits.length < 7) return "Phone number is too short.";
  if (digits.length > 15) return "Phone number is too long (max 15 digits).";
  return "";
}
function validateMessage(v) {
  if (!v.trim()) return "Message is required.";
  if (v.trim().length < 10) return "Message must be at least 10 characters.";
  return "";
}

// ── Reusable error/success line ──
function FieldFeedback({ error, touched, value }) {
  if (error && touched) return (
    <div style={{ color:"#e53e3e", fontSize:12, marginTop:5, display:"flex", alignItems:"center", gap:5 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      {error}
    </div>
  );
  if (!error && touched && value) return (
    <div style={{ color:"#38a169", fontSize:12, marginTop:5, display:"flex", alignItems:"center", gap:5 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      Looks good!
    </div>
  );
  return null;
}

export default function Contact() {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const validate = (data) => ({
    name: validateName(data.name),
    email: validateEmail(data.email),
    phone: validatePhone(data.phone),
    message: validateMessage(data.message),
  });

  const handleChange = (key, value) => {
    const updated = { ...formData, [key]: value };
    setFormData(updated);
    if (touched[key]) {
      const errs = validate(updated);
      setErrors(prev => ({ ...prev, [key]: errs[key] }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    const errs = validate(formData);
    setErrors(prev => ({ ...prev, [key]: errs[key] }));
  };

  const handleSubmit = () => {
    const allTouched = { name: true, email: true, phone: true, message: true };
    setTouched(allTouched);
    const errs = validate(formData);
    setErrors(errs);
    if (Object.values(errs).some(e => e !== "")) return;
    const subject = encodeURIComponent(`New Enquiry from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || "N/A"}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:info@uniqueengineers.ae?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "", message: "" });
    setTouched({});
    setErrors({});
    setTimeout(() => setSubmitted(false), 6000);
  };

  const inputStyle = (key) => ({
    width: "100%",
    padding: "12px 14px",
    border: `1.5px solid ${errors[key] && touched[key] ? "#e53e3e" : touched[key] && !errors[key] && formData[key] ? "#38a169" : "#b8d8f0"}`,
    borderRadius: 8,
    fontSize: 14,
    color: "#0d3c6e",
    outline: "none",
    background: errors[key] && touched[key] ? "#fff5f5" : touched[key] && !errors[key] && formData[key] ? "#f0fff4" : "#fff",
    boxSizing: "border-box",
    transition: "border-color 0.2s, background 0.2s",
    fontFamily: "inherit",
  });

  const fields = [
    { label: "Your Name *", key: "name", type: "text", placeholder: "John Smith" },
    { label: "Email Address *", key: "email", type: "email", placeholder: "john@example.com" },
    { label: "Phone Number", key: "phone", type: "tel", placeholder: "+971 xx xxx xxxx" },
  ];

  return (
    <div style={{ fontFamily:"'Barlow',sans-serif", background:"#f0f7ff", minHeight:"100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes fadeLeft { from { opacity:0; transform:translateX(-40px); } to { opacity:1; transform:translateX(0); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        @media(max-width:768px){ .contact-grid{ grid-template-columns:1fr !important; } .map-grid{ grid-template-columns:1fr !important; } }
      `}</style>

      <Navbar />

      {/* ── HERO BANNER ── */}
      <section style={{ position:"relative", height:340, overflow:"hidden", display:"flex", alignItems:"center", paddingTop:72 }}>
        <img src={img13} alt="Contact" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", objectPosition:"center" }} />
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(135deg, rgba(13,60,110,0.82) 0%, rgba(26,106,181,0.65) 100%)" }} />
        <div style={{ position:"relative", zIndex:2, maxWidth:1200, margin:"0 auto", padding:"0 24px", opacity:visible?1:0, animation:visible?"fadeLeft 0.8s ease forwards":"none" }}>
          <h1 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:"clamp(40px,6vw,72px)", fontWeight:900, color:"#fff", margin:"0 0 14px", textTransform:"uppercase", letterSpacing:-1 }}>Contact Us</h1>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <a href="/" style={{ color:"rgba(255,255,255,0.65)", fontSize:14, textDecoration:"none" }}
              onMouseEnter={e => e.currentTarget.style.color="#fff"}
              onMouseLeave={e => e.currentTarget.style.color="rgba(255,255,255,0.65)"}
            >Home</a>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            <span style={{ color:"#e8a800", fontSize:14, fontWeight:600 }}>Contact Us</span>
          </div>
        </div>
      </section>

      {/* ── CONTACT SECTION ── */}
      <section style={{ background:"#fff", padding:"88px 24px" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ color:"#1a6ab5", fontWeight:700, fontSize:12, letterSpacing:3, textTransform:"uppercase", marginBottom:12 }}>Get In Touch</div>
            <h2 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:"clamp(30px,4vw,48px)", fontWeight:900, color:"#0d3c6e", margin:"0 0 14px", textTransform:"uppercase" }}>Contact Us</h2>
            <p style={{ color:"#3a6080", fontSize:15, maxWidth:480, margin:"0 auto" }}>Have a project in mind? Reach out and our team will get back to you promptly.</p>
          </div>

          <div className="contact-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:32 }}>

            {/* Info Panel */}
            <div style={{ background:"linear-gradient(135deg,#1a6ab5,#0d3c6e)", borderRadius:16, padding:"40px 36px", animation:visible?"fadeLeft 0.9s ease forwards":"none", opacity:visible?1:0 }}>
              <h3 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:"#e8a800", margin:"0 0 32px", textTransform:"uppercase" }}>Contact Information</h3>
              {[
                { icon:(<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e8a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>), label:"Address", val:"Ras Al Khor Industrial Area 1, Dubai, UAE" },
                { icon:(<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e8a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.37 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.74a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>), label:"Phone", val:"050 7861818" },
                
                { icon:(<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e8a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>), label:"Email", val:"info@uniqueengineers.ae" },
              ].map(({ icon, label, val }) => (
                <div key={label} style={{ display:"flex", gap:16, marginBottom:24, alignItems:"flex-start" }}>
                  <div style={{ width:40, height:40, background:"rgba(255,255,255,0.1)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{icon}</div>
                  <div>
                    <div style={{ color:"#e8a800", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{label}</div>
                    <div style={{ color:"#cce0f5", fontSize:14 }}>{val}</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop:8, padding:"16px 20px", background:"rgba(255,255,255,0.08)", borderRadius:10, borderLeft:"3px solid #e8a800" }}>
                <div style={{ color:"#e8a800", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>P.O. Box</div>
                <div style={{ color:"#cce0f5", fontSize:14 }}>390694, Dubai, UAE</div>
              </div>
            </div>

            {/* Validated Form */}
            <div style={{ background:"#f0f7ff", borderRadius:16, padding:"40px 36px", border:"1px solid #cce0f5", animation:visible?"fadeUp 0.9s 0.2s ease forwards":"none", opacity:visible?1:0 }}>
              <h3 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:800, fontSize:22, color:"#0d3c6e", margin:"0 0 28px", textTransform:"uppercase" }}>Send a Message</h3>

              {/* Success banner */}
              {submitted && (
                <div style={{ background:"#e6ffed", border:"1.5px solid #38a169", borderRadius:8, padding:"12px 16px", marginBottom:20, color:"#276749", fontSize:14, fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Your email client has opened! Message will be sent to info@uniqueengineers.ae
                </div>
              )}

              {fields.map(({ label, key, type, placeholder }) => (
                <div key={key} style={{ marginBottom:18 }}>
                  <label style={{ display:"block", color:"#0d3c6e", fontSize:12, fontWeight:700, letterSpacing:0.5, marginBottom:7, textTransform:"uppercase" }}>{label}</label>
                  <input
                    type={type}
                    placeholder={placeholder}
                    value={formData[key]}
                    onChange={e => handleChange(key, e.target.value)}
                    onBlur={() => handleBlur(key)}
                    style={inputStyle(key)}
                  />
                  <FieldFeedback error={errors[key]} touched={touched[key]} value={formData[key]} />
                </div>
              ))}

              <div style={{ marginBottom:24 }}>
                <label style={{ display:"block", color:"#0d3c6e", fontSize:12, fontWeight:700, letterSpacing:0.5, marginBottom:7, textTransform:"uppercase" }}>Message *</label>
                <textarea
                  placeholder="Tell us about your project..."
                  rows={5}
                  value={formData.message}
                  onChange={e => handleChange("message", e.target.value)}
                  onBlur={() => handleBlur("message")}
                  style={{ ...inputStyle("message"), resize:"vertical" }}
                />
                <FieldFeedback error={errors.message} touched={touched.message} value={formData.message} />
              </div>

              <button
                onClick={handleSubmit}
                style={{ width:"100%", background:"#1a6ab5", color:"#fff", fontWeight:800, fontSize:14, letterSpacing:1.5, padding:"14px", borderRadius:8, border:"none", cursor:"pointer", textTransform:"uppercase", transition:"background 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.background="#1558a0"; e.currentTarget.style.transform="translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background="#1a6ab5"; e.currentTarget.style.transform="translateY(0)"; }}
              >Send Message →</button>
              
            </div>
          </div>
        </div>
      </section>

      {/* ── MAP SECTION ── */}
      <section style={{ background:"#f0f7ff", padding:"0" }}>
        <div className="map-grid" style={{ maxWidth:1200, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1.6fr", minHeight:420 }}>
          <div style={{ background:"linear-gradient(160deg,#0d3c6e 0%,#1a6ab5 100%)", padding:"56px 48px", display:"flex", flexDirection:"column", justifyContent:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
              <div style={{ width:48, height:48, background:"rgba(232,168,0,0.15)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", border:"1px solid rgba(232,168,0,0.3)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e8a800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <h3 style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:900, fontSize:28, color:"#fff", margin:0, textTransform:"uppercase", letterSpacing:1 }}>Find Us</h3>
            </div>
            {[
              { label:"Address", val:"Ras Al Khor Industrial Area 1, Dubai, UAE" },
              { label:"Phone", val:"050 7861818" },
              
            ].map(({ label, val }) => (
              <div key={label} style={{ marginBottom:20 }}>
                <div style={{ color:"#e8a800", fontSize:10, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", marginBottom:3 }}>{label}</div>
                <div style={{ color:"#cce0f5", fontSize:14, lineHeight:1.5 }}>{val}</div>
              </div>
            ))}
            <a href="https://maps.google.com/?q=Ras+Al+Khor+Industrial+Area+1+Dubai+UAE" target="_blank" rel="noreferrer"
              style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#e8a800", color:"#0d3c6e", fontWeight:800, fontSize:13, letterSpacing:1.5, padding:"12px 24px", borderRadius:8, textDecoration:"none", textTransform:"uppercase", transition:"background 0.2s, transform 0.15s", marginTop:8, alignSelf:"flex-start" }}
              onMouseEnter={e => { e.currentTarget.style.background="#d09800"; e.currentTarget.style.transform="translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="#e8a800"; e.currentTarget.style.transform="translateY(0)"; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d3c6e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              Open in Google Maps
            </a>
          </div>
          <div style={{ position:"relative", minHeight:420 }}>
            <iframe
              title="Unique Engineers Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3610.178548232847!2d55.3489!3d25.1855!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5f5f5f5f5f5f%3A0x1234567890abcdef!2sRas%20Al%20Khor%20Industrial%20Area%201%2C%20Dubai!5e0!3m2!1sen!2sae!4v1234567890"
              width="100%" height="100%"
              style={{ border:0, display:"block", minHeight:420 }}
              allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}